/**
 * Enquadramento ("voltar à Amazônia"), zoom pelos botões e sincronização da
 * vista com `useViewStore`.
 */
import type OlMap from 'ol/Map';
import type { Coordinate } from 'ol/coordinate';
import type { Extent } from 'ol/extent';
import { getCenter, getHeight } from 'ol/extent';
import { useViewStore } from '../state/view';
import { GOES_MIN_RESOLUTION } from './createMap';
import { amazoniaLegalExtent, mapToLonLat, NSA_EXTENT } from './projection';
import { celularDeitado, FIM_DO_TRILHO, noCelular } from '../ui/layoutCelular';
import { trilhoRecolhido } from '../ui/trilhoRecolhido';

/** Trilho de ícones (desktop, px). */
export const RAIL_WIDTH = 64;
/** Painel da categoria (desktop, px), entre o trilho e o mapa. */
export const SIDEBAR_WIDTH = 340;
/** Cartão do inspetor ("Últimos focos"), flutuando sobre a borda direita do mapa (desktop, px). */
export const INSPECTOR_WIDTH = 280;
export const MOBILE_BREAKPOINT = 768;

/** Vista resultante do último "voltar à Amazônia" (para saber se ainda estamos nela). */
let homeState: { center: Coordinate; resolution: number } | null = null;

/**
 * Largura (px) do painel lateral que flutua sobre a borda esquerda do mapa no
 * desktop (`ui/Sidebar`), ou 0 recolhido. Lido do DOM: é o que de fato cobre
 * o mapa, sem `viewControl` depender do estado da interface.
 */
export function painelSobreOMapa(): number {
  if (typeof document === 'undefined') return 0;
  const el = document.querySelector<HTMLElement>('[data-painel-lateral]');
  return el ? el.getBoundingClientRect().width : 0;
}

/**
 * Margens para os chips que flutuam sobre o mapa: [cima, direita, baixo,
 * esquerda]. A barra de tempo fica fora da área do mapa; o painel lateral
 * flutua sobre a borda esquerda dele e entra na margem da esquerda.
 * - Desktop: em cima, a linha de chips (marca/ações/zoom, 36 px); embaixo, o
 *   chip da legenda; à direita, a coluna do zoom.
 * - Celular em pé: em cima, chip da marca e botão "voltar" (44 px); embaixo,
 *   o play (48 px). O trilho e a coluna dos lados ficam por conta de quem
 *   enquadra uma área (`enquadrarExtensao`): o setor inteiro é mais largo que
 *   a tela e passa por baixo deles de qualquer jeito.
 * - Celular deitado: o trilho de camadas na esquerda (até 102 px; 60 px
 *   recolhido), a coluna na direita (64 px), o chip em cima e a assinatura
 *   embaixo.
 *
 * O celular é o da regra do `useIsMobile` (`CONSULTA_CELULAR`), e não só a
 * largura: deitado, o Pixel 7 tem 844 px e caía nas margens do desktop.
 */
export function panelPadding(): [number, number, number, number] {
  if (noCelular()) return celularDeitado() ? [56, 64, 36, trilhoRecolhido() ? FIM_DO_TRILHO.emPe : FIM_DO_TRILHO.deitado] : [12 + 44 + 8, 12, 12 + 48 + 12, 12];
  // À direita: a coluna do zoom (36 px) ou a aba fina do inspetor (28 px).
  return [12 + 36 + 8, 56, 36, 12 + painelSobreOMapa()];
}

/**
 * Enquadra uma área (município da busca, áreas do filtro dos focos): com folga
 * para o que flutua sobre o mapa (painel lateral, coluna de botões, barra de
 * tempo) e aproximando até o zoom máximo do SATÉLITE (`GOES_MIN_RESOLUTION`,
 * ~220 m por pixel) — antes o limite era 1,5× a resolução nativa da imagem de
 * 7200 px, e um município como Santarém ficava com poucas centenas de pixels
 * no meio do mapa (reclamação do usuário, 08/10/2026). `embaixo`: px a mais no
 * pé (uma folha aberta no celular).
 */
export function enquadrarExtensao(map: OlMap, extensao: Extent, opcoes: { embaixo?: number; duracao?: number } = {}): void {
  const view = map.getView();
  view.cancelAnimations();
  const [cima, direita, baixo, esquerda] = panelPadding();
  // No celular em pé, a coluna de botões da direita e o trilho de camadas da esquerda (44 px cada,
  // com a borda) também ocupam o mapa; deitado, `panelPadding` já os desconta.
  const emPe = noCelular() && !celularDeitado();
  const folga = 24;
  view.fit(extensao, {
    padding: [cima + folga, direita + folga + (emPe ? 52 : 0), baixo + folga + (opcoes.embaixo ?? 0), esquerda + folga + (emPe ? 48 : 0)],
    duration: opcoes.duracao ?? 650,
    minResolution: GOES_MIN_RESOLUTION,
  });
}

function recordHome(map: OlMap): void {
  const view = map.getView();
  const center = view.getCenter();
  const resolution = view.getResolution();
  homeState = center && resolution ? { center: [...center], resolution } : null;
  useViewStore.setState({ isHome: true });
}

function isAtHome(map: OlMap): boolean {
  if (!homeState) return false;
  const view = map.getView();
  const c = view.getCenter();
  const r = view.getResolution();
  if (!c || !r) return false;
  const tol = r * 2;
  return (
    Math.abs(c[0] - homeState.center[0]) < tol &&
    Math.abs(c[1] - homeState.center[1]) < tol &&
    Math.abs(r / homeState.resolution - 1) < 0.01
  );
}

/**
 * Quanto o enquadramento inicial aproxima além do que caberia a Amazônia Legal
 * inteira.
 *
 * Antes a vista cabia de Acre a São Luís com folga, e o detalhe se perdia: uma
 * trilha de radiossonda de 20 km ocupava 5 px, e as gotas da chuva viravam
 * confete. Aproximar 50% custa uma faixa nas pontas leste e oeste — o botão
 * "voltar à Amazônia" e o zoom para fora continuam alcançando o resto.
 */
const APROXIMACAO_HOME = 1.5;

/**
 * Enquadra a Amazônia Legal, descontando os botões que flutuam sobre o mapa e
 * aproximando `APROXIMACAO_HOME` para o detalhe ser legível. Em telas em pé o
 * setor não cobre a altura toda — cortamos leste/oeste antes do norte-sul.
 */
export function flyHome(map: OlMap, duration = 600): void {
  const view = map.getView();
  const size = map.getSize();
  const extent = amazoniaLegalExtent();
  const padding = panelPadding();
  if (!size) return;
  const fitSize = [Math.max(1, size[0] - padding[1] - padding[3]), Math.max(1, size[1] - padding[0] - padding[2])];
  let fitRes = view.getResolutionForExtent(extent, fitSize) / APROXIMACAO_HOME;
  // Celular em pé com "preencher": a imagem 5:3 é limitada pela largura e
  // sobraria faixa preta em cima e embaixo. Aproxima até a altura do setor
  // preencher a área livre (corta um pouco de leste/oeste, nunca o norte-sul
  // da Amazônia).
  const portrait = size[1] > size[0];
  if (portrait && useViewStore.getState().fillMode === 'preencher') {
    fitRes = Math.min(fitRes, getHeight(NSA_EXTENT) / fitSize[1]);
  }
  const resolution = view.getConstrainedResolution(fitRes) ?? fitRes;
  // Centro da Amazônia deslocado para o meio da área livre (fora dos painéis).
  const c = getCenter(extent);
  const center = view.getConstrainedCenter(
    [c[0] + ((padding[1] - padding[3]) / 2) * resolution, c[1] + ((padding[0] - padding[2]) / 2) * resolution],
    resolution,
  );
  if (duration > 0) {
    view.animate({ center, resolution, duration }, () => recordHome(map));
  } else {
    view.setResolution(resolution);
    view.setCenter(center);
    recordHome(map);
  }
}

export function zoomBy(map: OlMap, delta: number): void {
  const view = map.getView();
  const zoom = view.getZoom();
  if (zoom === undefined) return;
  view.animate({ zoom: view.getConstrainedZoom(zoom + delta), duration: 250 });
}

/** Publica centro/zoom/resolução no `useViewStore` a cada `moveend`. */
export function startViewSync(map: OlMap): () => void {
  const update = () => {
    const view = map.getView();
    const center = view.getCenter();
    const lonLat = center ? mapToLonLat(center[0], center[1]) : null;
    const valid = !!lonLat && Number.isFinite(lonLat[0]) && Number.isFinite(lonLat[1]);
    useViewStore.setState((s) => ({
      center: valid ? [lonLat![0], lonLat![1]] : s.center,
      zoom: view.getZoom() ?? null,
      resolution: view.getResolution() ?? null,
      isHome: isAtHome(map),
    }));
  };
  map.on('moveend', update);
  return () => map.un('moveend', update);
}
